// src/pages/bicc.jsx
import React, { useEffect, useState } from "react";
import { getAllMasjidData } from "../services/dynamicApiService";
import { getHijriDate } from "../services/hijriService";
import { getMasjidServices } from "../services/moduleService";
import { getPrayerTimes } from "../services/masjidService";

// pages
import HomePage from "./homepage";
import AboutPage from "./aboutpage";
import ServicesPage from "./Servicespage";
import EventsPage from "./eventpage";
import ContactPage from "./contactpage";

// assets used in header/footer
import logo from "../assets/Bramalea2.png";

const DEFAULT_MASJID_ID = Number(process.env.REACT_APP_MASJID_ID || 36);
const MASJID_ID = Number.isFinite(DEFAULT_MASJID_ID) ? DEFAULT_MASJID_ID : 36;

/* ------------ helpers ------------ */
const normalizeEvent = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const startDate =
    raw.startDate ?? raw.start_date ?? raw.date ?? raw.start ?? raw.startOn ?? null;
  const endDate =
    raw.endDate ?? raw.end_date ?? raw.until ?? raw.repeatEndDate ?? raw.end ?? null;

  return {
    ...raw,
    name: raw.name ?? raw.title ?? "Event",
    title: raw.title ?? raw.name ?? "Event",
    startDate: startDate || null,
    endDate: endDate || startDate || null,
    time: raw.time ?? raw.startTime ?? raw.startsAt ?? null,
    location: raw.location ?? raw.venue ?? raw.place ?? null,
    image: raw.image,
  };
};

// Tiny preloader just for the app shell
const Preloader = ({ isLoading }) => {
  if (!isLoading) return null;
  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center">
      <div className="mb-5 h-16 w-16 bg-green-700 rounded-full flex items-center justify-center">
        <span className="text-white font-bold text-lg">BICC</span>
      </div>
      <div className="h-1 w-20 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-green-700 animate-pulse"></div>
      </div>
    </div>
  );
};




const Header = ({ currentPage, setCurrentPage, isMenuOpen, setIsMenuOpen }) => {
  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "services", label: "Services And Program" },
    { id: "events", label: "Events" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <header className="header bg-white shadow-sm sticky top-0 z-40">
      <nav className="navbar">
        <div className="w-full px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <button onClick={() => setCurrentPage("home")} className="navbar-logo flex items-center">
              <img src={logo} alt="BICC Logo" className="h-14 w-18 mr-3" />
              <div>
                <h2 className="text-lg font-bold">Bramalea Islamic</h2>
                <p className="text-sm font-bold text-black">Cultural Centre</p>
              </div>
            </button>

            {/* Mobile button */}
            <button className="md:hidden border-0 p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <span className="text-[#D2B995] text-bold text-xl">☰</span>
            </button>

            {/* Nav */}
            <div
              className={`${isMenuOpen ? "block" : "hidden"} md:flex md:items-center md:space-x-6 absolute md:static top-16 left-0 right-0 bg-white md:bg-transparent shadow-md md:shadow-none p-4 md:p-0`}
            >
              <ul className="space-y-4 md:space-y-0 md:flex md:space-x-6">
                {navItems.map((item) => (
                  <li key={item.id} className="relative group">
                    <button
                      onClick={() => {
                        setCurrentPage(item.id);
                        setIsMenuOpen(false);
                      }}
                      className={`relative text-lg font-bold transition-colors duration-300 ${currentPage === item.id ? "text-[#D2B995]" : "text-gray-700 hover:text-[#D2B995]"
                        }`}
                    >
                      {item.label}
                      <span
                        className={`absolute left-0 -bottom-1 h-[2px] w-full bg-[#D2B995] transition-transform duration-300 origin-left ${currentPage === item.id ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                          }`}
                      />
                    </button>
                  </li>
                ))}
              </ul>

              {/* Donate */}
              <div className="mt-4 md:mt-0 md:ml-6">
                <a
                  href="https://app.irm.io/bramaleaicc.ca"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#D2B995] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:bg-[#C4A882] hover:text-black"
                >
                  Donate Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

const Footer = ({ setCurrentPage }) => {
  const navItems = [
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "services", label: "Services And Program" },
    { id: "events", label: "Events" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <footer className="bg-[#F7ECD9] mt-16">
      <div className="w-full px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* About */}
          <div>
            <button onClick={() => setCurrentPage("home")} className="mb-4">
              <div className="flex items-center">
                <img src={logo} alt="BICC Logo" className="h-14 w-14 rounded-full mr-3 border-2 bg-white object-contain" />
                <div>
                  <h2 className="text-lg font-bold">Bramalea Islamic</h2>
                  <p className="text-sm text-black">Cultural Centre</p>
                </div>
              </div>
            </button>
            <p className="text-gray-700 mb-4">
              Islam is about knowing what and how to live your life in the best way.
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-3">
                <span className="text-green-700 mt-1">📍</span>
                <span>25 Kings Cross Rd B, Brampton, ON L6T 3V5, Canada</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-green-700 mt-1">✉️</span>
                <a href="mailto:bicc@bramaleaicc.ca" className="hover:text-[#A67C37]">bicc@bramaleaicc.ca</a>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-green-700 mt-1">📞</span>
                <a href="tel:6477800667" className="hover:text-[#A67C37]">647 780 0667</a>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h6 className="text-lg font-semibold mb-4">Quick Links</h6>
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.id} className="flex items-center space-x-2">
                  <span className="text-[#A67C37] text-sm">›</span>
                  <button
                    onClick={() => setCurrentPage(item.id)}
                    className="text-gray-700 hover:text-[#A67C37] transition-colors text-left"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact form (dummy) */}
          <div>
            <h6 className="text-lg font-semibold mb-4">Contact us</h6>
            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              <input className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-700" placeholder="Full Name" />
              <input type="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-700" placeholder="Your Email" />
              <textarea className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-green-700" placeholder="Write Message" rows="4"></textarea>
              <button className="w-full bg-[#C4A882] text-white py-3 px-6 rounded-lg hover:bg-gray transition-colors font-semibold">Send Now</button>
            </form>
          </div>
        </div>
      </div>
      <div className="bg-[#d8c0a2] py-4">
        <div className="w-full px-4 text-center">
          <span className="text-gray-700">© 2025. All rights reserved by BICC.</span>
        </div>
      </div>
    </footer>
  );
};

const BICCWebsite = () => {
  const [currentPage, setCurrentPage] = useState("home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // dynamic data shared with pages
  const [prayerTimes, setPrayerTimes] = useState({
    // bootstrap defaults; replaced by API once it loads
    // iqamatimes: {
    //   fajr: "06:30 AM",
    //   dhuhr: "02:00 PM",
    //   asr: "05:45 PM",
    //   maghrib: "Sunset",
    //   isha: "09:00 PM",
    //   jumuah1_khutba: "1:40 PM",
    //   jumuah1_iqamah: "2:20 PM",
    // },
    // startTimes: {
    //   timings: {
    //     Fajr: "05:40 AM",
    //     Sunrise: "07:16 AM",
    //     Dhuhr: "01:09 PM",
    //     Asr: "04:26 PM",
    //     Maghrib: "Sunset",
    //     Isha: "08:37 PM",
    //   },
    // },
  });
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]); // <— no static default
  const [hijriDate, setHijriDate] = useState({ formatted: "" });
  const [dataLoading, setDataLoading] = useState(true);

  // expose nav for debugging
  useEffect(() => {
    window.currentPage = { setCurrentPage };
  }, [setCurrentPage]);

  // fetch services for Home page sections (API only, no static fallback)
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const svc = await getMasjidServices(MASJID_ID, { signal: ac.signal });
        setServices(Array.isArray(svc) ? svc : []);
      } catch (e) {
        console.warn("Services API failed:", e?.message);
        setServices([]); // leave empty if error
      }
    })();
    return () => ac.abort();
  }, []);

  // fetch combined data (prayers, events, services?, hijri)
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setDataLoading(true);
        const data = await getAllMasjidData(MASJID_ID);

        const data1 = await getPrayerTimes(MASJID_ID);
        if (data1) setPrayerTimes(data1);

        if (Array.isArray(data?.events) && data.events.length) {
          const norm = data.events.map(normalizeEvent).filter(Boolean);
          setEvents(norm);
        }



        if (data?.hijriDate?.formatted) setHijriDate(data.hijriDate);
      } catch (err) {
        console.error("getAllMasjidData failed:", err);
        // keep whatever services we already have from the services call
      } finally {
        setDataLoading(false);
      }
    };
    fetchAllData();
  }, []);

  // hijri (client side best-effort)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const d = await getHijriDate();
        if (mounted && d) setHijriDate(d);
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, []);

  // small splash timeout
  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(t);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return (
          <HomePage
            setCurrentPage={setCurrentPage}
            prayerTimes={prayerTimes}
            events={events}
            services={services}
            hijriDate={hijriDate}
            dataLoading={dataLoading}
          />
        );
      case "about":
        return <AboutPage setCurrentPage={setCurrentPage} />;
      case "services":
        // IMPORTANT: ServicesPage will fetch its own data; no 'services' prop passed
        return <ServicesPage setCurrentPage={setCurrentPage} masjidId={MASJID_ID} />;
      case "events":
        return <EventsPage setCurrentPage={setCurrentPage} masjidId={MASJID_ID} />;
      case "contact":
        return <ContactPage setCurrentPage={setCurrentPage} />;
      default:
        return (
          <HomePage
            setCurrentPage={setCurrentPage}
            prayerTimes={prayerTimes}
            events={events}
            services={services}
            hijriDate={hijriDate}
            dataLoading={dataLoading}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Preloader isLoading={isLoading || dataLoading} />
      <Header
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />
      <main className="pb-4">{renderPage()}</main>
      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
};

export default BICCWebsite;
